<template>
  <!-- Äußeren Container so definieren, dass er den ganzen Bildschirm (oder einen definierten Bereich) einnimmt -->
  <div class="p-5 bg-trader-gray rounded flex flex-col gap-2 flex-grow">

    <!-- Info-Bereich -->
    <div class="flex flex-col text-black pb-5">
      <h2 class="text-2xl font-bold">Standard Excel Import</h2>
      <span class="text-md">
        Dies ist der Standard-Excel-Import. Bitte wählen Sie die Datei aus, die Sie importieren möchten.
      </span>
      <span class="text-md">
        Sie können normale sowie auch regionale Magazine importieren.
      </span>
    </div>

    <!-- SELECTS (und weitere Eingaben) -->
    <div class="flex flex-col w-full gap-2">
      <!-- Datei -->
      <div class="flex">
        <span class="p-1 w-[150px]">Datei:</span>
        <label @change="onFileChange"
               class="text-center bg-trader-dark-gray hover:bg-trader-blue hover:text-white flex-grow rounded p-2">
          <input type="file" class="hidden">
          Datei auswählen
        </label>
      </div>

      <!-- Projekt -->
      <div class="flex">
        <span class="p-1 w-[150px]">Projekt:</span>
        <select
            class="outline-0 border border-bg-trader-input-border border-header p-1 flex-grow shadow-inner-2 text-gray-500"
            v-model="projectId">
          <option v-for="project in projectData.projects"
                  :key="project.projectId"
                  :value="project.projectId">
            {{ project.name }}
          </option>
        </select>
      </div>

      <!-- Magazine -->
      <div class="flex">
        <span class="p-1 w-[150px]">Magazine:</span>
        <select v-model="magazineId"
                class="outline-0 border border-bg-trader-input-border border-header p-1 flex-grow shadow-inner-2 text-gray-500">
          <option v-for="magazine in magazineData"
                  :key="magazine.magazineId"
                  :value="magazine.magazineId">
            {{ magazine.name }}
          </option>
        </select>
      </div>

      <!-- Einkaufsbereich -->
      <div class="flex">
        <span class="p-1 w-[150px]">Auswahl des Einkaufsbereichs:</span>
        <select v-model="purchaserId"
                class="outline-0 border border-bg-trader-input-border border-header p-1 flex-grow shadow-inner-2 text-gray-500">
          <option :value="0">Alle Einkaufsbereiche</option>
          <option v-for="purchaser in purchasers"
                  :value="purchaser.id"
                  :key="purchaser.id">
            {{ purchaser.teamIcon }} {{ purchaser.name }}
          </option>
        </select>
      </div>

      <div class="flex">
        <span class="p-1 w-[150px]">Ziel Einkaufsbereich auswählen:</span>
        <select v-model="goalPurchaserId"
                class="outline-0 border border-bg-trader-input-border border-header p-1 flex-grow shadow-inner-2 text-gray-500">
          <option :value="0">passende</option>
          <option v-for="purchaser in purchasers"
                  :value="purchaser.id"
                  :key="purchaser.id">
            {{ purchaser.teamIcon }} {{ purchaser.name }}
          </option>
        </select>
      </div>


      <!-- Checkbox -->
      <div class="flex items-center">
        <span class="p-1">Position ohne EKB berücksichtigen</span>
        <input type="checkbox"
               class="outline-0 border border-bg-trader-input-border border-header p-1 shadow-inner-2 text-gray-500"
               v-model="ignorePurchasers">
      </div>
    </div>


    <div v-show="!importTrue"
         class="flex flex-grow-0 p-4 bg-trader-light-gray rounded-lg shadow-lg min-h-[200px] overflow-hidden">
      <div class=" overflow-auto">
        <table v-if="fileData.length" class="w-full table-auto">
          <thead>
          <tr>
            <th v-for="(field, idx) in fileData[0].customFields" :key="idx"
                class=" whitespace-nowrap font-semibold min-w-[100px] p-2 bg-trader-dark-gray border-b border-trader-gray"
                :title="field.field">

              <div class="flex flex-col gap-2">
                <select class="w-full text-left" v-model="field.field" :disabled="!userData?.isAdmin">
                  <option v-for="productCustomField in productCustomFields" :key="productCustomField.id"
                          :value="productCustomField.fieldName">
                    {{ productCustomField.fieldName }}
                  </option>
                </select>
              </div>
            </th>
          </tr>
          </thead>

          <tbody>
          <tr v-for="(data, index) in fileData" :key="index"
              class="hover:bg-trader-gray transition-colors ">
            <td v-for="(field, idx) in data.customFields" :key="idx"
                class="whitespace-nowrap text-left text-black p-2"
                :title="field.value">
              {{ field.value }}
            </td>
          </tr>

          </tbody>

        </table>
      </div>
    </div>

    <!-- Bereich für Fortschrittsanzeige, Seiten und Buttons -->
    <div class="flex-shrink-0 h-[130px] gap-2 flex flex-col">
      <div class="flex flex-col justify-between gap-2">
        <div class="flex flex-col gap-2">
          <!-- Fortschrittsanzeige -->
          <TRProgress v-show="importTrue" :progress="processesBar"></TRProgress>

          <span>Seiten:</span>
          <div class="grid grid-cols-8 gap-2">
            <template v-for="page in pages" :key="page.pagePosition">
              <div class="flex p-2 cursor-pointer"
                   @click="getPagePositions(page.pagePosition)"
                   :class="{
                   'bg-trader-blue text-white': pagePositions.includes(page.pagePosition),
                   'bg-trader-dark-gray hover:bg-trader-darker-gray': !pagePositions.includes(page.pagePosition)
                 }">
                <span class="text-center w-full">{{ page.pagePosition }}</span>
              </div>
            </template>
          </div>

          <div class="flex gap-2 w-full">
            <button class="text-center bg-trader-dark-gray hover:bg-trader-blue hover:text-white w-1/2 rounded p-2"
                    @click="selectAllPagePosition">
              Alle Seiten auswählen
            </button>
            <button class="text-center bg-trader-dark-gray hover:bg-trader-blue hover:text-white w-1/2 rounded p-2"
                    @click="deSelectAllPagePosition">
              Alle Seiten abwählen
            </button>
          </div>
        </div>
      </div>

      <!-- Buttons am Ende -->
      <div class="flex gap-1 justify-between items-center">
        <button class="text-center bg-trader-dark-gray hover:bg-trader-blue hover:text-white rounded p-2 min-w-[150px]">
          Abbrechen
        </button>
        <button @click="uploadFile"
                class="text-trader-dark-red text-center bg-trader-red hover:bg-trader-blue hover:text-white rounded p-2 min-w-[150px]">
          Jetzt importieren
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {eventBus} from "@/application/EventBus.js";
import apolloClient from "@/apolloClient";
import {gql} from "@apollo/client/core";
import axios from "axios";
import Spinner from "@/components/Base/Spinner.vue";
import SignalBus from "@/application/SignalBus";
import {mutation, query} from "@/graphql";
import ShowReport from "@/components/Dialogs/ShowReport.vue";
import TRProgress from "@/components/Base/v2/TRProgress.vue";
import {mapState} from "vuex";

export default {
  components: {TRProgress, Spinner},

  props: {
    value: {
      type: Object,
      default: () => ({})
    },
  },
  data() {
    return {
      projectData: [],
      projectId: 0,
      magazineId: 0,
      magazineData: [],
      purchasers: [],
      purchaserId: 0,
      ignorePurchasers: false,
      selectedFile: null,
      pages: [],
      pagePositions: [1],
      loading: false,
      jopId: 1,
      processesBar: 0,
      mapColumnToCustomFields: {},
      fileData: [],
      randOmNumber: 0,
      importTrue: false,
      productCustomFields: [],
      goalPurchaserId: 0,
    };
  },
  computed: {
    ...mapState('user', ['userData']),
  },
  mounted() {
    this.getProjects();
    this.getPurchaser();
    this.getPages();
    this.getAllProductFields();
  },
  created() {
    this.projectId = this.$route.params.projectId;
    this.magazineId = this.$route.params.magazineId;
  },
  methods: {

    // get all produktFields
    async getAllProductFields() {

      try {
        const [data, err] = await query.getProductCustomFields()
        this.productCustomFields = data
        
      } catch (error) {
        console.error("Fehler beim Abrufen der Produktfelder:", error);
      }

    },

    async onFileChange(event) {
      this.selectedFile = event.target.files[0];
      if (this.selectedFile) {
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        formData.append('pagePositions', this.pagePositions)
        formData.append('purchaserId', this.purchaserId);
        formData.append('ignorePurchasers', this.ignorePurchasers);

        const response = await axios.post('/api/v3/excel/import/magazine/' + this.magazineId, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (response.data) {
          console.log("response", response.data);
          this.fileData = this.formateResponse(response.data);
          
        }
      }
    },
    selectAllPagePosition() {
      this.pagePositions = this.pages.map(page => page.pagePosition);
    },
    deSelectAllPagePosition() {
      this.pagePositions = [];
    },
    getPagePositions(pagePosition) {
      if (this.pagePositions.includes(pagePosition)) {
        this.pagePositions = this.pagePositions.filter(position => position !== pagePosition);
      } else {
        this.pagePositions.push(pagePosition);
      }
    },
    async getJop(jopId) {
      const [data, err] = await query.getJop(jopId);
      if (err) {
        console.error('Fehler beim Abrufen des JOP:', err);
      }
      this.processesBar = data?.processed;
      if (data?.processed === 100 && data.report.errors.length > 0) {
        eventBus.openModal(ShowReport, {
          title: "Report Meldung",
          errors: data?.report.errors,
          messages: data?.report.messages,
        });
      } else if (data?.processed === 100) {
        location.reload();
      }
    },

    async uploadFile() {
      if (!this.selectedFile) {
        alert('Bitte wählen Sie eine Datei aus.');
        return;
      }
      const formData = new FormData();
      formData.append('file', this.selectedFile);
      formData.append('pagePositions', this.pagePositions)
      formData.append('purchaserId', this.purchaserId);
      formData.append('ignorePurchasers', this.ignorePurchasers);
      formData.append('goalPurchaserId', this.goalPurchaserId);
      try {
        const response = await axios.post('/api/v3/excel/import/magazine/' + this.magazineId, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data) {
          await this.excelImport(response.data);
        }

      } catch (error) {
        console.error('Fehler beim Hochladen der Datei:', error);
        alert('Fehler beim Hochladen der Datei.');
      } finally {
        this.loading = false;
        SignalBus.emit('reload-page');
      }
    },


    formatPrice(num) {
      let str = "" + num;
      str = str.replace(',', '.')
      const s = parseFloat(str).toFixed(3);
      if (s.endsWith('0')) {
        return s.slice(0, -1);
      }
      return s;
    },
    formateResponse(excelData) {
      let importData = [];

      let marketColumns = [];

      if (excelData.fileSuffix.toLowerCase().endsWith(".xls")) {
        console.log("formatResponse für .xls");

        // 1) Mapping für Custom Fields
        const mapColumnToCustomField = {
          1: "product_name",
          2: "description_short",
          3: "quantity",
          4: "quantity_2nd_field",
          5: "unit",
          6: "package_type",
          7: "price_per_unit",
          8: "price",
          9: "has_warranty",
          10: "purchaser_notes",
        };
        this.mapColumnToCustomFields = mapColumnToCustomField;

        // 2) Welche Werte in Spalte 0 kennzeichnen eine Header-Zeile?
        const headerValues = ["FAN", "SAP", "0"];
        const HEADER_IDX = 0;

        excelData.data.forEach((row, rowIndex) => {
          // Safe trim: falls row[0] undefined ist, nutzen wir den leeren String
          const cell = (row[HEADER_IDX] || "").trim().toUpperCase();

          // 3) Header- und Leerzeilen überspringen
          if (cell === "" || headerValues.includes(cell)) {
            // bei einer tatsächlichen Header-Zeile sammeln wir die Markt-Spaltennamen
            if (headerValues.includes(cell)) {
              for (let i = 11; i < row.length; i++) {
                marketColumns.push(row[i]);
              }
            }
            return;
          }

          // 4) Datenzeile: Produktnummer, Custom Fields und Märkte anlegen
          let tmp = {
            productNumber: cell,
            customFields: [],
            markets: [],
          };

          // 4a) Custom Fields füllen
          Object.keys(this.mapColumnToCustomFields).forEach((colIdx) => {

            // if price is empty, set it to 0
            let value = row[colIdx];
            if (this.mapColumnToCustomFields[colIdx] === "price") {
              value = this.formatPrice(value);
            }

            tmp.customFields.push({
              field: this.mapColumnToCustomFields[colIdx],
              value: value,
            });
          });

          // 4b) Märkte: ab Offset 11 alle marketColumns abgleichen
          const marketOffset = 11;
          marketColumns.forEach((marketName, idx) => {
            const raw = (row[marketOffset + idx] || "").trim().toLowerCase();
            tmp.markets.push({
              marketName,
              placed: raw.includes("x"),
              regional: raw.includes("a"),
            });
          });

          importData.push(tmp);
        });
      } else {

        const mapColumnToCustomField = {
          2: "product_name",
          3: "description_short",
          4: "quantity",
          5: "quantity_2nd_field",
          6: "unit",
          7: "package_type",
          8: "price_per_unit",
          9: "price",
          10: "has_warranty",
        }
        this.mapColumnToCustomFields = mapColumnToCustomField;
        const header = {
          "ART. NR": 0
        }
        excelData.data.forEach((row, rowIndex) => {
          if (row[header["ART. NR"]].trim() === "" || row[header["ART. NR"]].trim() === "ART. NR") {

            if (row[header["ART. NR"]].trim() === "ART. NR") {
              for (let i = 11; i < row.length; i++) {
                marketColumns.push(row[i]);
              }
            }
            return;
          }
          if (rowIndex < 3) {
            return;
          }

          let tmp = {
            productNumber: row[header["ART. NR"]],
            customFields: [],
            markets: [],
          }


          Object.keys(this.mapColumnToCustomFields).forEach((columnIndex) => {
            let value = row[columnIndex];
            if (this.mapColumnToCustomFields[columnIndex] === "price") {
              value = this.formatPrice(value);
            }
            
            tmp.customFields.push({
              field: this.mapColumnToCustomFields[columnIndex],
              value: value,
            });
          });

          const marketOffset = 11;
          for (let i = 0; i < marketColumns.length; i++) {

            let value = row[marketOffset + i] || "";

            tmp.markets.push({
              marketName: marketColumns[i],
              placed: value.trim().toLowerCase().indexOf('x') >= 0,
              regional: value.trim().toLowerCase().indexOf('a') >= 0,
            });
          }

          importData.push(tmp);

        });

      }
      return importData;
    },


    async excelImport(excelData) {
      this.importTrue = true;
      this.fileData.forEach(data => {
        data.customFields.forEach(field => {
          // value is null or undefined make it empty string
          if (field.value === null || field.value === undefined) {
            field.value = "";
          }
        });
      })
      const [data, err] = await mutation.excelImport(this.magazineId, this.pagePositions, this.purchaserId, this.ignorePurchasers, this.fileData, this.goalPurchaserId);
      const intervalId = setInterval(() => {
        this.getJop(data?.jopId);
        if (this.processesBar === 100) {
          clearInterval(intervalId);
        }
      }, 2000);

      if (err) {
        console.error('Fehler beim Importieren der Excel-Datei:', err);
      }
    },
    async getPages() {
      const response = await apolloClient.query({
        query: gql`
        query pages($filter: QueryFilter) {
          pages(filter: $filter) {
            pageId
            pagePosition

          }
        }
      `,
        variables: {
          filter: {
            magazineId: this.magazineId,
            // TODO bei regional muss der marketId übergeben werden bei nicht regional 0
            marketId: 2,
          }
        }
      });
      try {
        this.pages = response.data.pages;

      } catch (error) {
        console.error("Fehler beim Abrufen der Pages:", error);
      }
    },
    async getPurchaser() {
      try {
        // Apollo Client Anfrage
        const {data} = await apolloClient.query({
          query: gql`
          query getPurchasers {
            getPurchasers {
              id
              name
              teamPosition
              teamIcon
              color
            }
          }
        `,
        });

        // Stellen Sie sicher, dass die Daten in shoppingSections gespeichert werden
        this.purchasers = data.getPurchasers;

        // Überprüfen, ob shoppingSections vorhanden und nicht leer ist
        if (this.shoppingSections && this.shoppingSections.length > 0) {
          // Speichern aller Farben in einer Variablen
          this.colors = this.purchaser.map(section => section.color);
        } else {

        }
      } catch (error) {
        console.error("Fehler beim Abrufen der ShoppingSections:", error.message);
      }
    },
    async getProjects() {
      try {
        const response = await apolloClient.query({
          query: gql`
            query GetProjectDetails {
              projects {
                projectId
                name
                magazines {
                  magazineId
                  name
                  numberOfPages

                }
              }
            }
          `
        });
        this.projectData = response.data;
        this.magazineData = [];
        if (this.projectId !== 0) {
          this.projectData.projects.forEach((project) => {
            if (project.projectId == this.projectId) {
              this.magazineData.push(project.magazines);
            }
          });
        } else {
          this.projectData.projects.forEach((project) => {
            this.magazineData.push(...project.magazines);
          });
        }

        this.magazineData = this.magazineData.flat();


      } catch (error) {
        console.error("Fehler beim Abrufen der Projektdaten:", error);
      }
    },

    deleteTrue() {
      eventBus.closeModal();
      try {
        if (this.value.onAccept) {

          eventBus.closeModal();
        }
      } catch (e) {
        console.error(e);
      }
    },
    deleteFalse() {
      eventBus.closeModal();
      try {
        if (this.value.onCancel) {
          this.value.onCancel();
        }
      } catch (e) {
        console.error(e);
      }
    }
  },
  watch: {
    projectId: 'getProjects',
    magazineId: 'getProjects',
    '$route.params': {
      handler: function (params) {
        if (this?.projectId != params?.projectId) {
          this.projectId = params.projectId;
        }
        if (this?.magazineId != params?.magazineId) {
          this.magazineId = params.magazineId;
        }
      },
      deep: true
    }

  }
};
</script>
